import React, { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import Input from './Input';

// Utility function to get the length of a formatted number
const getLength = (value) => new Intl.NumberFormat().format(value).length;

// Handle number input, including cursor position adjustment
const handleNumberInput = (inputValue, group, properties, name, start, end, input) => {
  const newCommaCheck = getLength(inputValue) - getLength(group.original) === 2;
  const rawNumber = parseInt(inputValue.replace(/,/g, ''), 10);
  const newValues = {
    original: inputValue,
    new: rawNumber * properties[name].multiples[group.step],
  };

  // Adjust cursor position after formatting
  setTimeout(() => {
    newCommaCheck ? input.setSelectionRange(start + 1, end + 1) : input.setSelectionRange(start, end);
  }, 0);

  return newValues;
};

// Handle percentage input with validation
const handlePercentageInput = (inputValue, group, properties, name) => {
  const validPattern = /^(\d{1,2}(\.\d?)?)?$/;
  if (validPattern.test(inputValue)) {
    const wasZero = group.original === '0';
    const startsWithDecimal = inputValue.startsWith('0.');
    const startsWithZero = /^0[0-9]/.test(inputValue);
    return {
      original: (wasZero && !startsWithDecimal) || startsWithZero ? inputValue.substring(1) : inputValue,
      new: inputValue * properties[name].multiples[group.step],
    };
  }
  return {};
};

// Handle currency input with validation and formatting
const handleCurrencyInput = (inputValue, group, properties, name) => {
  if (inputValue.startsWith('.')) inputValue = '0' + inputValue;
  if (inputValue.startsWith('0') && inputValue.length > 1 && inputValue[1] !== '.') inputValue = inputValue.slice(1);
  if (inputValue.split('.').length > 2) inputValue = inputValue.replace(/\.+$/, '');

  const parts = inputValue.split('.');
  if (parts[1] && parts[1].length > 2) inputValue = parts[0] + '.' + parts[1].slice(0, 2);

  return {
    original: inputValue,
    step: inputValue === '0.00' ? 0 : group.step,
    new: inputValue === '0.00' ? 0.0 : parseFloat(inputValue) * properties[name].multiples[group.step],
  };
};

export default function Form({ calculatorValues, setCalculatorValues, section, properties }) {
  const { ref, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // Handle input changes
  const handleInput = useCallback(
    (e, name, type) => {
      const input = e.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      let inputValue = input.value.replace(/[^0-9.]/g, '');
      if (inputValue === '') inputValue = '0';
      const validValue = /^\d*\.?\d*$/;

      if (!validValue.test(inputValue)) return null;

      setCalculatorValues((prevValues) =>
        prevValues.map((group) => {
          if (group.name !== name) return group;

          let newValues = {};
          if (type === 'number') {
            newValues = handleNumberInput(inputValue, group, properties, name, start, end, input);
          } else if (type === 'percentage') {
            newValues = handlePercentageInput(inputValue, group, properties, name);
          } else if (type === 'currency') {
            newValues = handleCurrencyInput(inputValue, group, properties, name);
          }

          return { ...group, ...newValues };
        })
      );
    },
    [setCalculatorValues, properties]
  );

  // Handle input blur events
  const handleBlur = useCallback(
    (e, name, type) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        setCalculatorValues((prevValues) => prevValues.map((group) => group));
      } else {
        if (type === 'number') {
          if (!inputValue) {
            setCalculatorValues((prevValues) =>
              prevValues.map((group) => (group.name === name ? { ...group, original: '0', new: '0' } : group))
            );
          }
        }

        if (type === 'percentage') {
          let value = inputValue.replace(/[-]/g, '');
          if (!value) {
            setCalculatorValues((prevValues) =>
              prevValues.map((group) => (group.name === name ? { ...group, original: `0.0`, new: 0.0 } : group))
            );
          } else if (!value.includes('.')) {
            setCalculatorValues((prevValues) =>
              prevValues.map((group) => (group.name === name ? { ...group, original: `${value}.0` } : group))
            );
          }
        }

        if (type === 'currency') {
          let value = inputValue.replace(/[,$]/g, '');

          if (value === '0' || value === '0.') {
            value = '0.00';
          } else if (!value.includes('.')) {
            value += '.00';
          } else {
            const parts = value.split('.');
            if (parts[1].length === 1) {
              value += '0';
            } else if (parts[1].length === 0) {
              value += '00';
            }
          }

          setCalculatorValues((prevValues) =>
            prevValues.map((group) =>
              group.name === name ? { ...group, original: value, new: value === '0.00' ? '0.00' : group.new } : group
            )
          );
        }
      }
    },
    [setCalculatorValues]
  );

  // Handle slider changes
  const handleSlider = useCallback(
    (e, name) => {
      const newStep = parseInt(e.target.value, 10);

      setCalculatorValues((prevValues) =>
        prevValues.map((group) => {
          return group.name === name
            ? {
                ...group,
                step: newStep,
                new: parseFloat(group.original) * properties[name].multiples[newStep],
              }
            : group;
        })
      );
    },
    [setCalculatorValues, properties]
  );

  // Update slider progress
  const updateSlider = (slider) => {
    const progress = (slider.value - slider.min) / (slider.max - slider.min);
    slider.style?.setProperty('--range-progress', progress);
  };

  // Effect to add event listeners to sliders
  useEffect(() => {
    const sliders = document.querySelectorAll('.calculator-slider');
    if (sliders) {
      sliders.forEach((slider) => {
        updateSlider(slider);
        slider.addEventListener('input', updateSlider);

        return () => {
          slider.removeEventListener('input', updateSlider);
        };
      });
    }
  }, [calculatorValues]);

  return (
    <div ref={ref}>
      {calculatorValues.map((metric, i) => (
        <Input
          key={i}
          metric={metric}
          properties={properties}
          section={section}
          handleInput={handleInput}
          handleBlur={handleBlur}
          handleSlider={handleSlider}
          index={i}
          focused={inView && i === 0}
        />
      ))}
    </div>
  );
}
